import React, { useState, useEffect } from 'react';
import { db } from '../utils/firebase';
import { collection, query, where, orderBy, getDocs, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import { useAuth } from '../hooks/useAuth';
import { useParams, Navigate } from 'react-router-dom';
import MemoryCard from '../components/MemoryCard';
import type { Memory } from '../components/MemoryCard';
import UserProfile from '../components/UserProfile';
import DreamModal from '../components/DreamModal';
import ProfileSettings from '../components/ProfileSettings';
import { motion, AnimatePresence } from 'framer-motion';

// Utility function to check if device is mobile
const isMobile = () => window.innerWidth <= 768;

// Star component with mobile optimization
const Star = ({ size, delay, duration }: { size: number; delay: number; duration: number }) => (
  <motion.div
    className="absolute rounded-full bg-dream-200"
    style={{
      width: size,
      height: size,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
    }}
    initial={{ opacity: 0 }}
    animate={{
      opacity: [0, 0.8, 0],
    }}
    transition={{
      duration: duration * (isMobile() ? 1.5 : 1),
      delay: delay,
      repeat: Infinity,
      repeatType: "reverse",
      ease: "linear",
    }}
  />
);

const ProfilePage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { user, loading } = useAuth();
  const [memories, setMemories] = useState<Memory[]>([]);
  const [selectedMemory, setSelectedMemory] = useState<Memory | null>(null);
  const [filter, setFilter] = useState<'all' | 'public' | 'private'>('all');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [showSettings, setShowSettings] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobileView, setIsMobileView] = useState(isMobile());

  const isCurrentUser = user && (!userId || user.uid === userId);
  const targetUserId = userId || user?.uid;

  useEffect(() => {
    const fetchUserMemories = async () => {
      if (!targetUserId) return;
      
      try {
        setIsLoading(true);
        const dreamsRef = collection(db, 'dreams');
        const constraints = [where('userId', '==', targetUserId)];
        
        if (!isCurrentUser) {
          constraints.push(where('isPublic', '==', true));
        }
        
        const q = query(dreamsRef, ...constraints);
        const querySnapshot = await getDocs(q);
        const fetchedMemories: Memory[] = [];

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          fetchedMemories.push({
            id: doc.id,
            ...data,
          } as Memory);
        });

        setMemories(fetchedMemories);
      } catch (error) {
        // Error handling without logging
        setMemories([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserMemories();
  }, [targetUserId, isCurrentUser]);

  useEffect(() => {
    const tags = new Set<string>();
    memories.forEach(memory => {
      memory.tags?.forEach(tag => tags.add(tag));
    });
    setAvailableTags([...tags]);
  }, [memories]);

  // Add resize listener
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(isMobile());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const filteredMemories = memories.filter(memory => {
    if (filter === 'all') return true;
    if (filter === 'public') return memory.isPublic;
    if (filter === 'private') return !memory.isPublic;
    return true;
  }).filter(memory => {
    if (!selectedTag) return true;
    return memory.tags.includes(selectedTag);
  });

  const handleMemorySelect = (memory: Memory) => {
    setSelectedMemory(memory);
  };

  const handleDelete = async (memoryId: string) => {
    const memoryToDelete = memories.find(m => m.id === memoryId);
    
    if (!user || !memoryToDelete || user.uid !== memoryToDelete.userId) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'dreams', memoryId));
      setMemories(memories.filter(memory => memory.id !== memoryId));
      setSelectedMemory(null);
    } catch (error) {
      // Error handling without logging
    }
  };

  const handleEdit = async (memory: Memory) => {
    if (!user || user.uid !== memory.userId) return;
    window.location.href = `/edit/${memory.id}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dream-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-dream-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-dream-950 via-dream-900 to-dream-950 overflow-hidden relative">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-dream-950/80 via-transparent to-dream-950/80"></div>
        
        {/* Generate stars */}
        {Array.from({ length: isMobileView ? 15 : 30 }).map((_, i) => (
          <Star
            key={`star-${i}`}
            size={Math.random() * 2 + 1}
            delay={Math.random() * 5}
            duration={Math.random() * 3 + 2}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-8 max-w-7xl relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <UserProfile 
            userId={userId} 
            onSettingsClick={isCurrentUser ? () => setShowSettings(true) : undefined} 
          />
        </motion.div>

        <motion.div 
          className="mt-8 sm:mt-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 sm:mb-8">
            <motion.h2 
              className="text-2xl sm:text-3xl font-bold text-dream-50"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              {isCurrentUser ? 'Your Dreams' : 'Shared Dreams'}
            </motion.h2>
            {isCurrentUser && (
              <motion.div 
                className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setFilter('all')}
                    className={`px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base 
                      ${filter === 'all'
                        ? 'bg-dream-500 text-dream-50 shadow-lg shadow-dream-500/20'
                        : 'bg-dream-700/50 text-dream-300 hover:bg-dream-600/50'
                      } transition-all duration-300 backdrop-blur-sm`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setFilter('public')}
                    className={`px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base 
                      ${filter === 'public'
                        ? 'bg-dream-500 text-dream-50 shadow-lg shadow-dream-500/20'
                        : 'bg-dream-700/50 text-dream-300 hover:bg-dream-600/50'
                      } transition-all duration-300 backdrop-blur-sm`}
                  >
                    Shared
                  </button>
                  <button
                    onClick={() => setFilter('private')}
                    className={`px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base 
                      ${filter === 'private'
                        ? 'bg-dream-500 text-dream-50 shadow-lg shadow-dream-500/20'
                        : 'bg-dream-700/50 text-dream-300 hover:bg-dream-600/50'
                      } transition-all duration-300 backdrop-blur-sm`}
                  >
                    Private
                  </button>
                </div>

                <select
                  value={selectedTag || ''}
                  onChange={(e) => setSelectedTag(e.target.value || null)}
                  className="px-3 sm:px-4 py-2 rounded-lg bg-dream-700/50 text-dream-200 
                           border border-dream-600/30 focus:ring-dream-500 focus:border-dream-500 
                           text-sm sm:text-base w-full sm:w-auto backdrop-blur-sm
                           hover:bg-dream-600/50 transition-all duration-300"
                >
                  <option value="">All Tags</option>
                  {availableTags.map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </select>
              </motion.div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <AnimatePresence>
              {filteredMemories.map((memory, index) => (
                <motion.div
                  key={memory.id}
                  initial={{ opacity: 0, y: 50, scale: 0.9 }}
                  animate={{ 
                    opacity: 1, 
                    y: 0, 
                    scale: 1,
                    transition: {
                      duration: 0.5,
                      delay: index * 0.1,
                      type: "spring",
                      stiffness: 100
                    }
                  }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  whileHover={{ 
                    y: -10,
                    scale: 1.02,
                    transition: { duration: 0.2 }
                  }}
                  className="relative"
                >
                  <div className="absolute -inset-1 bg-gradient-to-r from-dream-500/20 to-dream-400/20 
                                rounded-lg blur opacity-0 group-hover:opacity-100 transition duration-500"></div>
                  <MemoryCard
                    memory={memory}
                    onClick={() => handleMemorySelect(memory)}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </motion.div>

        {selectedMemory && (
          <DreamModal
            memory={selectedMemory}
            onClose={() => setSelectedMemory(null)}
            onDelete={isCurrentUser && user?.uid === selectedMemory.userId ? handleDelete : undefined}
            onEdit={isCurrentUser && user?.uid === selectedMemory.userId ? () => handleEdit(selectedMemory) : undefined}
          />
        )}

        {showSettings && (
          <ProfileSettings onClose={() => setShowSettings(false)} />
        )}
      </div>
    </div>
  );
};

export default ProfilePage; 